/**
 * ArticleDetailDrilldown — Modal overlay showing full article detail
 * with reviewer feedback, comments, and prev/next navigation.
 *
 * Task 6.1: Create modal component
 * Requirements: 11.1, 11.2, 11.3, 11.6
 */
import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { ReviewProgressArticle, computeTotalComments } from './progressReportUtils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SubmissionComment {
  id: string;
  commenterName: string;
  commenterId?: string | null;
  text: string;
  isRichText?: boolean;
  commentType?: string;
  createdAt: string;
}

export interface ArticleDetailDrilldownProps {
  article: ReviewProgressArticle;
  articles: ReviewProgressArticle[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

// ── Status badge colors (matching parent matrix component) ────────────────────

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Draft: { bg: '#f3f4f6', text: '#6b7280' },
  Submitted: { bg: '#dbeafe', text: '#1d4ed8' },
  UnderReview: { bg: '#fef3c7', text: '#d97706' },
  RevisionRequested: { bg: '#fee2e2', text: '#dc2626' },
  Approved: { bg: '#dcfce7', text: '#16a34a' },
  ReadyForLayout: { bg: '#f3e8ff', text: '#9333ea' },
  Published: { bg: '#d1fae5', text: '#065f46' },
};

// ── Date/time formatting ──────────────────────────────────────────────────────

function formatTimestamp(isoStr: string): string {
  const datePart = isoStr.split('T')[0];
  const timePart = isoStr.split('T')[1]?.slice(0, 5);
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (!timePart) return dateStr;
  const [hours, minutes] = timePart.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${dateStr}, ${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function formatStatusLabel(status: string): string {
  return status.replace(/([a-z])([A-Z])/g, '$1 $2');
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '720px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  header: {
    padding: '1.5rem 1.5rem 1rem',
    borderBottom: '1px solid #e5e7eb',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  articleTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a2e23',
    margin: 0,
    lineHeight: 1.3,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  meta: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    marginTop: '0.5rem',
    flexWrap: 'wrap',
  },
  metaItem: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  summaryLine: {
    marginTop: '0.75rem',
    fontSize: '0.85rem',
    color: '#374151',
    fontWeight: 500,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '1rem 1.5rem',
  },
  sectionTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.75rem',
    marginTop: '0.25rem',
  },
  reviewerCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    marginBottom: '0.75rem',
  },
  reviewerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.25rem',
  },
  reviewerName: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#1f2937',
  },
  reviewerStatus: {
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  reviewerStatusCompleted: {
    color: '#16a34a',
  },
  reviewerStatusPending: {
    color: '#d97706',
  },
  commentItem: {
    padding: '0.5rem 0',
    borderTop: '1px solid #f3f4f6',
    marginTop: '0.5rem',
  },
  commentMeta: {
    fontSize: '0.7rem',
    color: '#6b7280',
    marginBottom: '0.25rem',
  },
  commentText: {
    fontSize: '0.825rem',
    color: '#374151',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  footer: {
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  navBtn: {
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    color: '#374151',
  },
  navBtnDisabled: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'not-allowed',
    color: '#d1d5db',
  },
  positionIndicator: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  closeBtnFooter: {
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.5rem 1.25rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  loadingState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.875rem',
  },
  errorState: {
    padding: '1rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#b91c1c',
    fontSize: '0.8rem',
    textAlign: 'center',
  },
  noComments: {
    fontSize: '0.8rem',
    color: '#9ca3af',
    fontStyle: 'italic',
    paddingLeft: '0.25rem',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function ArticleDetailDrilldown({
  article,
  articles,
  currentIndex,
  onClose,
  onNavigate,
}: ArticleDetailDrilldownProps) {
  const [comments, setComments] = useState<SubmissionComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentsError, setCommentsError] = useState('');

  // ── Fetch comments when article changes ───────────────────────

  const fetchComments = useCallback(async (submissionId: string) => {
    setCommentsLoading(true);
    setCommentsError('');
    try {
      const data = await apiClient<SubmissionComment[]>(
        `/weekend-booklet/submissions/${submissionId}/comments`,
      );
      setComments(data);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Unable to load comments';
      setCommentsError(message);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComments(article.id);
  }, [article.id, fetchComments]);

  // ── Keyboard: Escape to close ─────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ── Focus trap: keep focus within the modal ────────────────────

  const panelRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    // Move focus to the first focusable element on mount
    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const firstFocusable = panel.querySelector<HTMLElement>(focusableSelector);
    if (firstFocusable) {
      firstFocusable.focus();
    }

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = panel.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab at first element → cycle to last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab at last element → cycle to first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, []);

  // ── Computed values ───────────────────────────────────────────

  const completedReviewers = article.reviewers.filter((r) => r.reviewed).length;
  const totalReviewers = article.reviewers.length;
  const totalComments = computeTotalComments(article);
  const statusColor = STATUS_COLORS[article.status] || { bg: '#f3f4f6', text: '#6b7280' };

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < articles.length - 1;

  // ── Get comments for a specific reviewer ──────────────────────

  const getReviewerComments = (firstName: string, lastName: string, userId?: string | null): SubmissionComment[] => {
    const fullName = `${firstName} ${lastName}`;
    return comments.filter((c) => {
      // Match by commenterId (userId) first — most reliable
      if (userId && c.commenterId && c.commenterId === userId) return true;
      // Fallback: match by commenterName (case-insensitive)
      return c.commenterName.toLowerCase() === fullName.toLowerCase();
    });
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div
      style={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Article detail: ${article.title}`}
    >
      <div style={styles.panel} ref={panelRef}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <h2 style={styles.articleTitle}>{article.title}</h2>
            <button
              style={styles.closeBtn}
              onClick={onClose}
              aria-label="Close article detail"
              title="Close"
            >
              ✕
            </button>
          </div>

          <div style={styles.meta}>
            {article.bookletSection && (
              <span style={styles.metaItem}>{article.bookletSection}</span>
            )}
            {article.authorName && (
              <span style={styles.metaItem}>by {article.authorName}</span>
            )}
            <span
              style={{
                ...styles.statusBadge,
                backgroundColor: statusColor.bg,
                color: statusColor.text,
              }}
            >
              {formatStatusLabel(article.status)}
            </span>
          </div>

          <div style={styles.summaryLine}>
            {completedReviewers} of {totalReviewers} reviewers complete, {totalComments} total comments
          </div>
        </div>

        {/* Body — Reviewer Cards */}
        <div style={styles.body}>
          <div style={styles.sectionTitle}>Reviewers</div>

          {article.reviewers.length === 0 && (
            <div style={styles.noComments}>No reviewers assigned</div>
          )}

          {article.reviewers.map((reviewer) => {
            const reviewerComments = commentsLoading
              ? []
              : getReviewerComments(reviewer.firstName, reviewer.lastName, reviewer.userId);

            return (
              <div key={reviewer.assignmentId} style={styles.reviewerCard}>
                <div style={styles.reviewerHeader}>
                  <span style={styles.reviewerName}>
                    {reviewer.firstName} {reviewer.lastName}
                  </span>
                  <span
                    style={{
                      ...styles.reviewerStatus,
                      ...(reviewer.reviewed
                        ? styles.reviewerStatusCompleted
                        : styles.reviewerStatusPending),
                    }}
                  >
                    {reviewer.reviewed
                      ? `Completed on ${reviewer.reviewedAt ? formatTimestamp(reviewer.reviewedAt) : 'N/A'}`
                      : `Pending (assigned ${formatTimestamp(reviewer.assignedAt)})`}
                  </span>
                </div>

                {/* Loading state for comments */}
                {commentsLoading && (
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                    Loading comments…
                  </div>
                )}

                {/* Error state for comments */}
                {commentsError && !commentsLoading && (
                  <div style={styles.errorState}>{commentsError}</div>
                )}

                {/* Reviewer's comments */}
                {!commentsLoading && !commentsError && reviewerComments.length > 0 && (
                  <div>
                    {reviewerComments.map((comment) => (
                      <div key={comment.id} style={styles.commentItem}>
                        <div style={styles.commentMeta}>
                          {comment.commenterName} · {formatTimestamp(comment.createdAt)}
                        </div>
                        <div
                          style={styles.commentText}
                          dangerouslySetInnerHTML={{ __html: comment.text }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* No comments for this reviewer */}
                {!commentsLoading && !commentsError && reviewerComments.length === 0 && (
                  <div style={styles.noComments}>No comments</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.footerLeft}>
            <button
              style={canGoPrev ? styles.navBtn : styles.navBtnDisabled}
              onClick={() => canGoPrev && onNavigate(currentIndex - 1)}
              disabled={!canGoPrev}
              aria-label="Previous article"
            >
              ← Prev
            </button>
            <span style={styles.positionIndicator}>
              {currentIndex + 1} of {articles.length}
            </span>
            <button
              style={canGoNext ? styles.navBtn : styles.navBtnDisabled}
              onClick={() => canGoNext && onNavigate(currentIndex + 1)}
              disabled={!canGoNext}
              aria-label="Next article"
            >
              Next →
            </button>
          </div>
          <button style={styles.closeBtnFooter} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
